import ErrorMessage from '@/components/errorMessage';
import { useApiContext } from '@/contexts/apiContext';
import { Box, Button, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProgressIndicator from '../progressIndicator';
import ReadOnlyField from '../readOnlyField';
import TabPanel from '../tabPanel';
import EmittedEventDefs from './emittedEventDefs';
import SettingDefs from './settingDefs';

type Props = {};

const UserAppDef = ({}: Props) => {
  const apiContext = useApiContext();
  const navigate = useNavigate();

  const { userAppDefId } = useParams();

  const [loading, setLoading] = useState(false);

  const [userAppDef, setUserAppDef] = useState<any>({});
  const [selectedTab, setSelectedTab] = useState<any>('emittedEventDefs');

  const [error, setError] = useState<any>();

  useEffect(() => {
    const getUserAppDef = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getUserAppDef(userAppDefId as string);
        setUserAppDef(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (userAppDefId) {
      getUserAppDef();
    }
  }, [userAppDefId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const close = () => {
    navigate('/administration?adminTab=userApps');
  };

  if (loading) {
    return <ProgressIndicator />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {userAppDef.name || 'User App Definition'}
        </Typography>
      </Box>

      {/* Info fields */}
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        {error && <ErrorMessage error={error} />}
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <ReadOnlyField label="Id" value={userAppDef.id} required type="text" />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <ReadOnlyField label="Name" value={userAppDef.name} required type="text" />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <ReadOnlyField label="Description" value={userAppDef.description} />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <ReadOnlyField label="Domain" value={userAppDef.domainId || 'general'} />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <ReadOnlyField label="Implementation Stack" required value={userAppDef.implementationStack} />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <ReadOnlyField label="Instance Icon" value={userAppDef.instanceIcon} type="text" />
          </Grid>
          <Grid size={12}>
            <ReadOnlyField label="Source agents def ids" value={userAppDef.sourceAgentDefs?.join()} type="text" />
          </Grid>
        </Grid>
      </Box>

      {/* Tabs */}
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        indicatorColor="secondary"
        variant="scrollable"
        sx={{ minHeight: 36, px: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <Tab
          value={'emittedEventDefs'}
          iconPosition="start"
          sx={{ minHeight: 36, py: 0 }}
          label={
            <Typography variant="caption" sx={{ fontWeight: selectedTab === 'emittedEventDefs' ? 700 : 500 }}>
              Events emitted
            </Typography>
          }
        />
        <Tab
          value={'settingDefs'}
          iconPosition="start"
          sx={{ minHeight: 36, py: 0 }}
          label={
            <Typography variant="caption" sx={{ fontWeight: selectedTab === 'settingDefs' ? 700 : 500 }}>
              Settings
            </Typography>
          }
        />
      </Tabs>

      {/* Tab content */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <TabPanel value="emittedEventDefs" currentTab={selectedTab}>
          <EmittedEventDefs emittedEventDefs={userAppDef.emittedEventDefs} />
        </TabPanel>
        <TabPanel value="settingDefs" currentTab={selectedTab}>
          <SettingDefs settingDefs={userAppDef.settingDefs} />
        </TabPanel>
      </Box>

      {/* Action bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}
      >
        <Button size="small" variant="text" onClick={close} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default UserAppDef;
